// A tiny immediate-mode UI drawn in the same low-res canvas as the game.

import { audio } from './audio';
import { drawText, textWidth, TextOpts } from './gfx/font';
import { input } from './input';

export const C = {
  bg: '#0c0810',
  panel: '#1c1420',
  panelL: '#2c2230',
  stone: '#3c3438',
  stoneL: '#5c5450',
  gold: '#c8a048',
  goldL: '#f8d878',
  goldD: '#785820',
  red: '#d82800',
  blue: '#2860f8',
  text: '#d8d0c0',
  dim: '#8c8478',
  white: '#fcfcfc',
  green: '#58d854',
  cyan: '#3cbcfc',
  orange: '#f87858',
  hot: '#403448',
};

export class UI {
  ctx!: CanvasRenderingContext2D;
  W = 480;
  H = 270;
  /** True if the cursor is on a panel this frame: the world shouldn't take its click. */
  captured = false;
  /** True if a panel drawn so far is under the cursor, so anything drawn before it is hidden there. */
  onPanel = false;
  private tooltipFn: (() => void) | null = null;
  focusId: string | null = null;

  begin(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    this.ctx = ctx;
    this.W = W;
    this.H = H;
    this.captured = false;
    this.onPanel = false;
    this.tooltipFn = null;
  }

  hit(x: number, y: number, w: number, h: number): boolean {
    return input.mx >= x && input.my >= y && input.mx < x + w && input.my < y + h;
  }

  rect(x: number, y: number, w: number, h: number, col: string): void {
    this.ctx.fillStyle = col;
    this.ctx.fillRect(x, y, w, h);
  }

  text(s: string, x: number, y: number, col: string = C.text, opts?: TextOpts): void {
    drawText(this.ctx, s, x, y, col, opts);
  }

  /** Stone panel with a double gold border, in the gothic ARPG idiom. */
  panel(x: number, y: number, w: number, h: number, title?: string): void {
    this.rect(x, y, w, h, '#000000');
    this.rect(x + 1, y + 1, w - 2, h - 2, C.goldD);
    this.rect(x + 2, y + 2, w - 4, h - 4, C.gold);
    this.rect(x + 3, y + 3, w - 6, h - 6, '#000000');
    this.rect(x + 4, y + 4, w - 8, h - 8, C.panel);
    // Subtle dither texture
    this.ctx.fillStyle = C.panelL;
    for (let py = y + 4; py < y + h - 4; py += 4) {
      for (let px = x + 4 + ((py >> 2) % 2) * 2; px < x + w - 4; px += 4) this.ctx.fillRect(px, py, 1, 1);
    }
    // Corner studs
    for (const [cx, cy] of [[x + 1, y + 1], [x + w - 4, y + 1], [x + 1, y + h - 4], [x + w - 4, y + h - 4]]) {
      this.rect(cx, cy, 3, 3, C.goldL);
    }
    if (this.hit(x, y, w, h)) {
      this.captured = true;
      this.onPanel = true;
    }
    if (title) {
      const tw = textWidth(title);
      this.rect(x + (w >> 1) - (tw >> 1) - 4, y + 1, tw + 8, 11, '#000000');
      this.rect(x + (w >> 1) - (tw >> 1) - 3, y + 2, tw + 6, 9, C.goldD);
      this.text(title, x + (w >> 1), y + 3, C.goldL, { align: 'center' });
    }
  }

  button(x: number, y: number, w: number, h: number, label: string, opts: { disabled?: boolean; col?: string; active?: boolean } = {}): boolean {
    const hot = !opts.disabled && this.hit(x, y, w, h);
    if (this.hit(x, y, w, h)) this.captured = true;
    const pressed = hot && input.lmb;
    this.rect(x, y, w, h, '#000000');
    this.rect(x + 1, y + 1, w - 2, h - 2, opts.disabled ? '#282028' : pressed ? C.goldD : opts.active ? C.gold : hot ? C.stoneL : C.stone);
    if (!opts.disabled && !pressed) this.rect(x + 1, y + 1, w - 2, 1, hot ? C.goldL : C.stoneL);
    const col = opts.disabled ? '#605858' : opts.active ? '#000000' : opts.col ?? (hot ? C.goldL : C.text);
    this.text(label, x + (w >> 1), y + ((h - 7) >> 1) + (pressed ? 1 : 0), col, { align: 'center', shadow: opts.active ? null : '#000000' });
    if (hot && input.lmbPressed) {
      audio.play('click');
      return true;
    }
    return false;
  }

  /** Single-line text field. Returns the new value. */
  field(id: string, x: number, y: number, w: number, value: string, opts: { max?: number; placeholder?: string; upper?: boolean } = {}): string {
    const h = 13;
    const hot = this.hit(x, y, w, h);
    if (hot) this.captured = true;
    if (hot && input.lmbPressed) this.focusId = id;
    const focused = this.focusId === id;
    this.rect(x, y, w, h, focused ? C.gold : C.goldD);
    this.rect(x + 1, y + 1, w - 2, h - 2, '#000000');
    let v = value;
    if (focused) {
      input.textMode = true;
      const max = opts.max ?? 20;
      for (const ch of input.typed) {
        if (v.length < max) v += opts.upper ? ch.toUpperCase() : ch;
      }
      if (input.pressed.has('Backspace')) v = v.slice(0, -1);
      input.onPaste = (s) => {
        // Applied next frame through typed[], one char at a time.
        for (const ch of s.slice(0, max)) input.typed.push(ch);
      };
    }
    if (!v && !focused && opts.placeholder) {
      this.text(opts.placeholder, x + 3, y + 3, C.dim, { shadow: null });
    } else {
      const cursor = focused && Math.floor(performance.now() / 400) % 2 === 0 ? '_' : '';
      this.text(v + cursor, x + 3, y + 3, C.white, { shadow: null });
    }
    return v;
  }

  tooltip(fn: () => void): void {
    this.tooltipFn = fn;
  }

  flushTooltip(): void {
    this.tooltipFn?.();
    this.tooltipFn = null;
  }

  /** Draws a multi-colored text box near the cursor, kept on screen. */
  textBox(lines: { s: string; c?: string }[], minW = 0): void {
    let w = minW;
    for (const l of lines) w = Math.max(w, textWidth(l.s));
    const bw = w + 10;
    const bh = lines.length * 9 + 8;
    let x = input.mx + 10;
    let y = input.my + 4;
    if (x + bw > this.W - 2) x = input.mx - bw - 6;
    if (x < 2) x = 2;
    if (y + bh > this.H - 2) y = this.H - bh - 2;
    if (y < 2) y = 2;
    this.boxAt(x, y, lines, bw, bh);
  }

  boxAt(x: number, y: number, lines: { s: string; c?: string }[], bw?: number, bh?: number): void {
    let w = 0;
    for (const l of lines) w = Math.max(w, textWidth(l.s));
    const width = bw ?? w + 10;
    const height = bh ?? lines.length * 9 + 8;
    this.rect(x, y, width, height, '#000000');
    this.rect(x + 1, y + 1, width - 2, height - 2, C.goldD);
    this.rect(x + 2, y + 2, width - 4, height - 4, '#080408');
    lines.forEach((l, i) => this.text(l.s, x + 5, y + 4 + i * 9, l.c ?? C.text));
  }
}

export const ui = new UI();
